import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { externalLinkOpen } from '../../../../actions/externallink';
import { RedirectServices, getRedirectUrl } from '../../../../config/urls';
import useMergeProps from '../../../../hooks/useMergeProps';
import useTypedSelector from '../../../../hooks/useTypedSelector';
import { getLang } from '../../../../i18n/l10n';
import RoutesRedirectionFactory from '../../../../utils/routes/RoutesRedirectionFactory';
import { startGR } from '../../actions/gr';
import { GRAssetsBySkinId } from '../../constants/assets';
import GRUIBySkinId from '../../constants/ui';
import { selectIsGREnabledAndNotForbidden } from '../../selectors/runtime.selector';
import { selectGRUserStatus } from '../../selectors/user/userPrimary.selector';
import { GRSkinId } from '../../types/global';
import { GROperationUserStatuses } from '../../types/user';

import GREnrollment from './GREnrollment';
import selectGREnrollmentProps from './GREnrollment.selector';

type StyledGREnrollmentPropsType = {
  $skinId: GRSkinId;
};
const StyledGREnrollment = styled(GREnrollment)<StyledGREnrollmentPropsType>`
  ${({ $skinId }) => css`
    --GREnrollment-logoImgPathUrl: url(${GRAssetsBySkinId[$skinId].Logos[getLang()]});
    --GREnrollment-listItemIconImgPathUrl: url(${GRAssetsBySkinId[$skinId].Enrollment.ListItemIconImgPath});
    --GREnrollment-startButtonBackroundImgPathUrl: url(${GRAssetsBySkinId[$skinId].Buttons.primary});
    --GREnrollment-backgroundImgPathUrl: url(${GRAssetsBySkinId[$skinId].Backgrounds?.enrollment});

    --GREnrollment-titleColor: ${GRUIBySkinId[$skinId].Informations?.primaryColor};
    --GREnrollment-listColor: ${GRUIBySkinId[$skinId].Informations?.secondaryColor};
    --GREnrollment-defaultColor: ${GRUIBySkinId[$skinId].Informations?.defaultColor};
  `}
`;

type GREnrollmentContainerProps = {
  className?: string;
};

const GREnrollmentContainer = (ownProps: GREnrollmentContainerProps) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const selectorProps = useMemo(() => ({ isLoading }), [isLoading]);
  const { skinId, ...selectedProps } = useTypedSelector((state) => selectGREnrollmentProps(state, selectorProps));

  const GROperationUserStatus = useSelector(selectGRUserStatus);
  const isGREnabled = useSelector(selectIsGREnabledAndNotForbidden);

  const onFullRulesClick = useCallback(() => {
    dispatch(externalLinkOpen(getRedirectUrl(RedirectServices.GR_RULES, { skinId })));
  }, [dispatch, skinId]);

  const onEnroll = useCallback(() => {
    setIsLoading(true);
  }, []);

  useEffect(() => {
    if (isLoading) {
      dispatch(
        startGR({
          hooks: {
            onFinally: () => {
              setIsLoading(false);
            },
          },
        }),
      );
    }
  }, [dispatch, isLoading]);

  const otherProps = useMemo(
    () => ({
      onEnroll,
      onFullRulesClick,
    }),
    [onEnroll, onFullRulesClick],
  );

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });

  if (isGREnabled) {
    if (GROperationUserStatus && GROperationUserStatus !== GROperationUserStatuses.OFF) {
      return <Redirect to={RoutesRedirectionFactory.getGRRoutePathname()} />;
    }

    return <StyledGREnrollment {...enhancedProps} $skinId={skinId} />;
  }

  return <Redirect to={RoutesRedirectionFactory.getHomeRoutePathname()} />;
};

export default GREnrollmentContainer;
